import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { IoSearch, IoClose, IoWalletOutline, IoTrashOutline, IoAddCircleOutline, IoDownloadOutline } from 'react-icons/io5';
import { ORDER_STATUS } from '../../../config/appConfig';
import { collection, onSnapshot, doc, updateDoc, deleteDoc, getDoc, setDoc, query, orderBy, Timestamp } from 'firebase/firestore';
import { db } from '../../../firebase/config';

const STATUS_KEYS = ['all', ...Object.keys(ORDER_STATUS)];

const fmtDateTime = (ts) => {
  if (!ts) return '-';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}.${pad(d.getMonth() + 1)}.${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const AdminOrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState('all');
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [quoteModal, setQuoteModal] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);
  const [bankModal, setBankModal] = useState(false);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [newBank, setNewBank] = useState({ bank: '', accountNo: '', holder: '' });
  const [excelModal, setExcelModal] = useState(false);
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  const [excelFrom, setExcelFrom] = useState(todayStr);
  const [excelTo, setExcelTo] = useState(todayStr);
  const PAGE_SIZE = 20;

  // 은행 계좌 로드
  const fetchBankAccounts = async () => {
    try {
      const snap = await getDoc(doc(db, 'settings', 'bankAccounts'));
      if (snap.exists()) {
        setBankAccounts(snap.data().accounts || []);
      }
    } catch (err) {
      console.error('계좌 로드 실패:', err);
    }
  };

  useEffect(() => {
    const q = query(collection(db, 'orders'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, (snap) => {
      const list = snap.docs.map(d => {
        const data = d.data();
        const productName = data.items?.length > 0
          ? (data.items.length === 1 ? data.items[0].name : `${data.items[0].name} 외 ${data.items.length - 1}건`)
          : '-';
        const totalQty = data.items?.reduce((s, item) => s + (item.qty || 0), 0) || 0;
        return {
          id: d.id,
          orderNo: data.orderNo || d.id,
          company: data.companyName || '-',
          product: productName,
          qty: totalQty,
          amount: data.quotedPrice || data.totalAmount || 0,
          status: data.status || 'requested',
          cancelledBy: data.cancelledBy || null,
          date: fmtDateTime(data.createdAt),
          statusChangedAt: fmtDateTime(data.statusChangedAt),
          items: data.items || [],
          memo: data.memo || '',
          address: data.address || null,
          invoiceRequested: data.invoiceRequested || false,
        };
      });
      setOrders(list);
      setLoading(false);
    }, (err) => {
      console.error('주문 구독 실패:', err);
      setLoading(false);
    });
    fetchBankAccounts();
    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = orders.filter(o => {
    if (tab !== 'all' && o.status !== tab) return false;
    if (search && !o.company.includes(search) && !o.orderNo.includes(search)) return false;
    return true;
  });

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const STATUS_MESSAGES = {
    requested: '주문요청 상태로 변경됩니다.\n사용자에게 "주문이 접수되었습니다"로 표시됩니다.',
    confirmed: '입금확인증 상태로 변경됩니다.\n사용자에게 "입금이 확인되었습니다"로 표시됩니다.',
    preparing: '배송준비중 상태로 변경됩니다.\n사용자에게 "상품 배송을 준비중입니다"로 표시됩니다.',
    shipped: '배송중 상태로 변경됩니다.\n사용자에게 "상품이 배송 출발했습니다"로 표시됩니다.',
    completed: '배송완료 상태로 변경됩니다.\n사용자에게 "배송이 완료되었습니다"로 표시됩니다.',
    cancelled: '관리자 취소 상태로 변경됩니다.\n사용자에게 "관리자에 의해 주문이 취소되었습니다"로 표시됩니다.',
  };

  const changeStatus = (id, newStatus) => {
    const order = orders.find(o => o.id === id);
    if (!order || order.status === newStatus) return;

    if (newStatus === 'quoted' || newStatus === 'requoted') {
      const items = (order.items || []).map(item => {
        const hasOptions = item.optionQtys && Object.keys(item.optionQtys).length > 0;
        return {
          productId: item.productId || item.id || '',
          name: item.name || '상품',
          qty: item.qty || 1,
          unitPrice: hasOptions ? '' : '',
          ...(item.optionQtys ? { optionQtys: item.optionQtys } : {}),
          ...(item.memo ? { memo: item.memo } : {}),
          ...(hasOptions ? { optionPrices: Object.fromEntries(Object.keys(item.optionQtys).map(k => [k, ''])) } : {}),
        };
      });
      setQuoteModal({ orderId: id, items, selectedAccount: bankAccounts[0]?.id || '', isRequote: newStatus === 'requoted' });
      return;
    }

    setConfirmModal({
      orderId: id,
      newStatus,
      message: STATUS_MESSAGES[newStatus] || `${ORDER_STATUS[newStatus]?.label} 상태로 변경됩니다.`,
      courier: '',
      trackingNo: '',
    });
  };

  const executeStatusChange = async () => {
    if (!confirmModal) return;
    const { orderId, newStatus, courier, trackingNo } = confirmModal;
    if (newStatus === 'shipped' && (!courier || !trackingNo)) return;
    try {
      const now = Timestamp.now();
      const updateData = { status: newStatus, statusChangedAt: now };
      if (newStatus === 'cancelled') {
        updateData.cancelledBy = 'admin';
        updateData.cancelReason = '관리자에 의한 취소';
      }
      if (newStatus === 'shipped') {
        updateData.courier = courier;
        updateData.trackingNo = trackingNo;
      }
      await updateDoc(doc(db, 'orders', orderId), updateData);
    } catch (err) {
      console.error('상태 변경 실패:', err);
    }
    setConfirmModal(null);
  };

  const submitQuote = async () => {
    if (!quoteModal) return;
    // 모든 항목에 단가가 입력되었는지 검증
    const hasEmptyPrice = quoteModal.items.some(item => {
      if (item.optionPrices && item.optionQtys) {
        return Object.keys(item.optionQtys).some(optVal => {
          const price = Number(String(item.optionPrices[optVal] || '').replace(/,/g, '')) || 0;
          return price <= 0;
        });
      }
      const price = Number(String(item.unitPrice).replace(/,/g, '')) || 0;
      return price <= 0;
    });
    if (hasEmptyPrice) return;

    const selectedAcc = bankAccounts.find(a => a.id === quoteModal.selectedAccount);
    if (!selectedAcc) return;

    const quotedItems = [];
    quoteModal.items.forEach(item => {
      if (item.optionPrices && item.optionQtys) {
        Object.entries(item.optionQtys).forEach(([optVal, optQty]) => {
          const unitPrice = Number(String(item.optionPrices[optVal] || '').replace(/,/g, ''));
          quotedItems.push({
            productId: item.productId,
            name: item.name,
            option: optVal,
            qty: optQty,
            unitPrice,
            subtotal: unitPrice * optQty,
          });
        });
      } else {
        const unitPrice = Number(String(item.unitPrice).replace(/,/g, ''));
        quotedItems.push({
          productId: item.productId,
          name: item.name,
          qty: item.qty,
          unitPrice,
          subtotal: unitPrice * item.qty,
        });
      }
    });
    const totalPrice = quotedItems.reduce((s, item) => s + item.subtotal, 0);

    try {
      const now = Timestamp.now();
      const newStatus = quoteModal.isRequote ? 'requoted' : 'quoted';
      const updateData = {
        status: newStatus,
        quotedItems,
        quotedPrice: totalPrice,
        bankAccount: { bank: selectedAcc.bank, accountNo: selectedAcc.accountNo, holder: selectedAcc.holder },
        statusChangedAt: now,
      };

      // 견적재수신: 기존 견적을 prevQuotes 배열에 보관
      if (quoteModal.isRequote) {
        const orderSnap = await getDoc(doc(db, 'orders', quoteModal.orderId));
        const orderData = orderSnap.data();
        if (orderData?.quotedItems && orderData?.quotedPrice) {
          const prevQuote = {
            quotedItems: orderData.quotedItems,
            quotedPrice: orderData.quotedPrice,
            bankAccount: orderData.bankAccount || null,
            createdAt: orderData.statusChangedAt || now,
          };
          const prevQuotes = [...(orderData.prevQuotes || []), prevQuote];
          updateData.prevQuotes = prevQuotes;
        }
      }

      await updateDoc(doc(db, 'orders', quoteModal.orderId), updateData);
      const nowStr = fmtDateTime(now);
      setOrders(prev => prev.map(o => o.id === quoteModal.orderId ? { ...o, status: newStatus, amount: totalPrice, statusChangedAt: nowStr } : o));
      if (modal?.id === quoteModal.orderId) setModal(prev => ({ ...prev, status: newStatus, statusChangedAt: nowStr }));
      setQuoteModal(null);
    } catch (err) {
      console.error('견적 저장 실패:', err);
    }
  };

  const quoteTotal = quoteModal?.items.reduce((s, item) => {
    if (item.optionPrices && item.optionQtys) {
      return s + Object.entries(item.optionQtys).reduce((os, [optVal, optQty]) => {
        const price = Number(String(item.optionPrices[optVal] || '').replace(/,/g, '')) || 0;
        return os + price * optQty;
      }, 0);
    }
    const price = Number(String(item.unitPrice).replace(/,/g, '')) || 0;
    return s + price * item.qty;
  }, 0) || 0;

  const allPricesFilled = quoteModal?.items.every(item => {
    if (item.optionPrices && item.optionQtys) {
      return Object.keys(item.optionQtys).every(optVal => {
        const price = Number(String(item.optionPrices[optVal] || '').replace(/,/g, '')) || 0;
        return price > 0;
      });
    }
    const price = Number(String(item.unitPrice).replace(/,/g, '')) || 0;
    return price > 0;
  }) ?? false;

  const formatPrice = (val) => {
    const num = String(val).replace(/[^\d]/g, '');
    return num ? Number(num).toLocaleString() : '';
  };

  const updateQuoteItem = (index, unitPrice) => {
    setQuoteModal(prev => ({
      ...prev,
      items: prev.items.map((item, i) => i === index ? { ...item, unitPrice: formatPrice(unitPrice) } : item),
    }));
  };

  const updateQuoteOptionPrice = (index, optVal, unitPrice) => {
    setQuoteModal(prev => ({
      ...prev,
      items: prev.items.map((item, i) => i === index ? {
        ...item,
        optionPrices: { ...item.optionPrices, [optVal]: formatPrice(unitPrice) },
      } : item),
    }));
  };

  // 은행 계좌 추가
  const addBankAccount = async () => {
    if (!newBank.bank || !newBank.accountNo || !newBank.holder) return;
    const account = { ...newBank, id: `ba_${Date.now()}` };
    const updated = [...bankAccounts, account];
    try {
      await setDoc(doc(db, 'settings', 'bankAccounts'), { accounts: updated });
      setBankAccounts(updated);
      setNewBank({ bank: '', accountNo: '', holder: '' });
    } catch (err) {
      console.error('계좌 추가 실패:', err);
    }
  };

  // 은행 계좌 삭제
  const deleteBankAccount = async (id) => {
    const updated = bankAccounts.filter(a => a.id !== id);
    try {
      await setDoc(doc(db, 'settings', 'bankAccounts'), { accounts: updated });
      setBankAccounts(updated);
    } catch (err) {
      console.error('계좌 삭제 실패:', err);
    }
  };

  const handleDeleteOrder = async (e, orderId) => {
    e.stopPropagation();
    if (!window.confirm('이 주문을 삭제하시겠습니까?')) return;
    try {
      await deleteDoc(doc(db, 'orders', orderId));
    } catch (err) {
      console.error('주문 삭제 실패:', err);
    }
  };

  const downloadExcel = () => {
    const from = new Date(excelFrom);
    from.setHours(0, 0, 0, 0);
    const to = new Date(excelTo);
    to.setHours(23, 59, 59, 999);
    const target = orders.filter(o => {
      if (o.status !== 'completed') return false;
      const dateStr = o.date?.replace(/\./g, '-').replace(' ', 'T');
      const d = new Date(dateStr);
      return d >= from && d <= to;
    });
    if (target.length === 0) {
      alert('해당 기간에 배송완료된 주문이 없습니다.');
      return;
    }
    const DELIVERY_LABELS = { parcel: '택배', quick: '퀵배송', pickup: '직접수령', freight: '화물배송' };
    const header = ['주문번호', '파트너', '상품명', '상품상세', '수량', '금액', '주문요청일', '상태변경일', '수령인', '연락처', '주소', '상세주소', '배송방법', '배송메시지', '메모', '계산서'];
    const rows = target.map(o => {
      const addr = o.address || {};
      const itemDetail = (o.items || []).map(item => {
        const opts = item.optionQtys ? Object.entries(item.optionQtys).map(([k, v]) => `${k}:${v}개`).join('/') : '';
        return `${item.name}${opts ? `(${opts})` : ''} x${item.qty}`;
      }).join(' | ');
      return [
        o.orderNo, o.company, o.product, itemDetail, o.qty, o.amount,
        o.date, o.statusChangedAt,
        addr.name || '', addr.phone || '', addr.address || '', addr.addressDetail || '',
        DELIVERY_LABELS[addr.deliveryMethod] || addr.deliveryMethod || '', addr.deliveryMessage || '',
        o.memo || '', o.invoiceRequested ? '발행' : '미발행',
      ];
    });
    const csvContent = [header, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `주문내역_${excelFrom}_${excelTo}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    setExcelModal(false);
  };

  const fmt = (n) => n.toLocaleString() + '원';

  const getStatusLabel = (o) => {
    if (o.status === 'cancelled') {
      return o.cancelledBy === 'admin' ? '관리자취소' : '사용자취소';
    }
    return ORDER_STATUS[o.status]?.label || o.status;
  };

  const getStatusColor = (o) => {
    if (o.status === 'cancelled' && o.cancelledBy === 'admin') return '#B71C1C';
    return ORDER_STATUS[o.status]?.color || '#8C9AA4';
  };

  return (
    <Container>
      <PageHeader>
        <SearchBox>
          <IoSearch size={18} color="#8C9AA4" />
          <SearchInput placeholder="주문번호, 파트너 검색" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
        </SearchBox>
        <HeaderBtnGroup>
          <ExcelBtn onClick={() => setExcelModal(true)}>
            <IoDownloadOutline size={16} />
            엑셀 다운로드
          </ExcelBtn>
          <BankMgmtBtn onClick={() => setBankModal(true)}>
            <IoWalletOutline size={16} />
            입금계좌 관리
          </BankMgmtBtn>
        </HeaderBtnGroup>
      </PageHeader>

      <TabRow>
        {STATUS_KEYS.map(k => {
          const label = k === 'all' ? '전체' : ORDER_STATUS[k].label;
          return (
            <Tab key={k} $active={tab === k} $color={k !== 'all' ? ORDER_STATUS[k].color : '#4A6741'} onClick={() => { setTab(k); setPage(1); }}>
              {label}
              {k !== 'all' && <TabCount>{orders.filter(o => o.status === k).length}</TabCount>}
            </Tab>
          );
        })}
      </TabRow>

      <TableWrap>
        <Table>
          <thead>
            <Tr><Th>주문번호</Th><Th>파트너</Th><Th>상품</Th><Th>수량</Th><Th>금액</Th><Th>상태</Th><Th>주문요청일</Th><Th>상태변경일</Th><Th>상태변경</Th></Tr>
          </thead>
          <tbody>
            {paged.map(o => (
              <Tr key={o.id} onClick={() => setModal(o)} style={{ cursor: 'pointer' }}>
                <Td $bold>{o.orderNo}</Td>
                <Td>{o.company}</Td>
                <TdProduct>{o.product}</TdProduct>
                <Td>{o.qty}개</Td>
                <Td>{fmt(o.amount)}</Td>
                <Td><Badge $color={getStatusColor(o)}>{getStatusLabel(o)}</Badge></Td>
                <Td $sub>{o.date}</Td>
                <Td $sub>{o.statusChangedAt}</Td>
                <Td onClick={e => e.stopPropagation()}>
                  {o.status === 'cancelled' ? (
                    <CancelledRow>
                      <CancelledText>{getStatusLabel(o)}</CancelledText>
                      <DeleteBtn onClick={(e) => handleDeleteOrder(e, o.id)}><IoTrashOutline size={14} /></DeleteBtn>
                    </CancelledRow>
                  ) : (
                    <Select value={o.status} onChange={e => changeStatus(o.id, e.target.value)}>
                      {Object.entries(ORDER_STATUS).map(([k, v]) => (
                        <option key={k} value={k}>{v.label}</option>
                      ))}
                    </Select>
                  )}
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
        {filtered.length === 0 && <Empty>해당 주문이 없습니다</Empty>}
      </TableWrap>

      {totalPages > 1 && (
        <Pagination>
          <PageBtn disabled={page === 1} onClick={() => setPage(1)}>«</PageBtn>
          <PageBtn disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹</PageBtn>
          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
            .reduce((acc, p, i, arr) => { if (i > 0 && p - arr[i - 1] > 1) acc.push('...'); acc.push(p); return acc; }, [])
            .map((p, i) => p === '...' ? <PageDots key={`dots-${i}`}>…</PageDots> : <PageNum key={p} $active={page === p} onClick={() => setPage(p)}>{p}</PageNum>)}
          <PageBtn disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>›</PageBtn>
          <PageBtn disabled={page === totalPages} onClick={() => setPage(totalPages)}>»</PageBtn>
          <PageInfo>{filtered.length}건 중 {(page - 1) * PAGE_SIZE + 1}-{Math.min(page * PAGE_SIZE, filtered.length)}</PageInfo>
        </Pagination>
      )}

      {modal && (
        <Overlay onClick={() => setModal(null)}>
          <Modal onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>주문 상세 — {modal.orderNo}</ModalTitle>
              <CloseBtn onClick={() => setModal(null)}><IoClose size={22} /></CloseBtn>
            </ModalHeader>
            <ModalBody>
              <DetailTable>
                <tbody>
                  <DRow><DLabel>파트너</DLabel><DVal>{modal.company}</DVal></DRow>
                  {modal.items.map((item, i) => (
                    <DRow key={i}>
                      <DLabel>{i === 0 ? '상품' : ''}</DLabel>
                      <DVal>
                        {item.name}
                        {item.optionQtys && Object.keys(item.optionQtys).length > 0 ? (
                          <DOptionList>{Object.entries(item.optionQtys).map(([v, q]) => `${v} ${q}개`).join(', ')}</DOptionList>
                        ) : (
                          <DOptionList>수량 {item.qty}개</DOptionList>
                        )}
                        {item.memo && <DOptionList>메모: {item.memo}</DOptionList>}
                      </DVal>
                    </DRow>
                  ))}
                  <DRow><DLabel>금액</DLabel><DVal>{fmt(modal.amount)}</DVal></DRow>
                  {modal.address && (
                    <DRow>
                      <DLabel>배송지</DLabel>
                      <DVal>
                        {modal.address.label && <DAddrLabel>{modal.address.label}</DAddrLabel>}
                        {modal.address.deliveryMethod && <DDeliveryTag>{{parcel:'택배',quick:'퀵배송',pickup:'직접수령',freight:'화물배송'}[modal.address.deliveryMethod] || modal.address.deliveryMethod}</DDeliveryTag>}
                        {modal.address.name} {modal.address.phone}<br />
                        {modal.address.address} {modal.address.addressDetail}
                        {modal.address.deliveryMessage && <DDeliveryMsg>{modal.address.deliveryMessage}</DDeliveryMsg>}
                      </DVal>
                    </DRow>
                  )}
                  {modal.memo && <DRow><DLabel>메모</DLabel><DVal>{modal.memo}</DVal></DRow>}
                  <DRow><DLabel>계산서</DLabel><DVal><InvoiceBadge $requested={modal.invoiceRequested}>{modal.invoiceRequested ? '발행' : '미발행'}</InvoiceBadge></DVal></DRow>
                  <DRow><DLabel>주문요청일</DLabel><DVal>{modal.date}</DVal></DRow>
                  <DRow><DLabel>상태변경일</DLabel><DVal>{modal.statusChangedAt}</DVal></DRow>
                  <DRow><DLabel>상태</DLabel><DVal><Badge $color={getStatusColor(modal)}>{getStatusLabel(modal)}</Badge></DVal></DRow>
                </tbody>
              </DetailTable>
            </ModalBody>
          </Modal>
        </Overlay>
      )}

      {/* 견적 입력 모달 */}
      {quoteModal && (
        <Overlay onClick={() => setQuoteModal(null)}>
          <QuoteModal onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{quoteModal.isRequote ? '견적서 재작성' : '견적서 작성'}</ModalTitle>
              <CloseBtn onClick={() => setQuoteModal(null)}><IoClose size={22} /></CloseBtn>
            </ModalHeader>
            <ModalBody>
              <QuoteTable>
                <thead>
                  <tr>
                    <QuoteTh $left>상품명</QuoteTh>
                    <QuoteTh>수량</QuoteTh>
                    <QuoteTh>단가</QuoteTh>
                    <QuoteTh>소계</QuoteTh>
                  </tr>
                </thead>
                <tbody>
                  {quoteModal.items.map((item, i) => {
                    if (item.optionPrices && item.optionQtys) {
                      const optEntries = Object.entries(item.optionQtys);
                      return optEntries.map(([optVal, optQty], oi) => {
                        const optPrice = Number(String(item.optionPrices[optVal] || '').replace(/,/g, '')) || 0;
                        const optSubtotal = optPrice * optQty;
                        return (
                          <tr key={`${i}-${oi}`}>
                            <QuoteTd $left>
                              {oi === 0 && <QuoteProductName>{item.name}</QuoteProductName>}
                              <QuoteOptionLabel>{optVal}</QuoteOptionLabel>
                            </QuoteTd>
                            <QuoteTd>{optQty}개</QuoteTd>
                            <QuoteTd>
                              <QuoteItemInput
                                placeholder="단가"
                                value={item.optionPrices[optVal]}
                                onChange={e => updateQuoteOptionPrice(i, optVal, e.target.value)}
                              />
                            </QuoteTd>
                            <QuoteTd>{optSubtotal > 0 ? optSubtotal.toLocaleString() + '원' : '-'}</QuoteTd>
                          </tr>
                        );
                      });
                    }
                    const unitPrice = Number(String(item.unitPrice).replace(/,/g, '')) || 0;
                    const subtotal = unitPrice * item.qty;
                    return (
                      <tr key={i}>
                        <QuoteTd $left>{item.name}</QuoteTd>
                        <QuoteTd>{item.qty}개</QuoteTd>
                        <QuoteTd>
                          <QuoteItemInput
                            placeholder="단가"
                            value={item.unitPrice}
                            onChange={e => updateQuoteItem(i, e.target.value)}
                          />
                        </QuoteTd>
                        <QuoteTd>{subtotal > 0 ? subtotal.toLocaleString() + '원' : '-'}</QuoteTd>
                      </tr>
                    );
                  })}
                </tbody>
              </QuoteTable>

              <QuoteTotalBar>
                <QuoteTotalLabel>합계</QuoteTotalLabel>
                <QuoteTotalValue>{quoteTotal.toLocaleString()}원</QuoteTotalValue>
              </QuoteTotalBar>

              <QuoteLabel>입금 계좌 선택</QuoteLabel>
              {bankAccounts.length > 0 ? (
                <AccountSelect
                  value={quoteModal.selectedAccount}
                  onChange={e => setQuoteModal(prev => ({ ...prev, selectedAccount: e.target.value }))}
                >
                  <option value="">계좌를 선택하세요</option>
                  {bankAccounts.map(a => (
                    <option key={a.id} value={a.id}>{a.bank} {a.accountNo} ({a.holder})</option>
                  ))}
                </AccountSelect>
              ) : (
                <NoAccountMsg>등록된 계좌가 없습니다. 입금계좌 관리에서 추가하세요.</NoAccountMsg>
              )}

              <QuoteBtnRow>
                <QuoteBtn $cancel onClick={() => setQuoteModal(null)}>취소</QuoteBtn>
                <QuoteBtn onClick={submitQuote} disabled={!allPricesFilled || !quoteModal.selectedAccount}>확인</QuoteBtn>
              </QuoteBtnRow>
            </ModalBody>
          </QuoteModal>
        </Overlay>
      )}

      {/* 입금계좌 관리 모달 */}
      {excelModal && (
        <Overlay onClick={() => setExcelModal(false)}>
          <ExcelModalBox onClick={e => e.stopPropagation()}>
            <ModalTitle>엑셀 다운로드</ModalTitle>
            <ExcelDesc>배송완료된 주문만 다운로드됩니다.</ExcelDesc>
            <ExcelDateRow>
              <ExcelDateLabel>시작일</ExcelDateLabel>
              <ExcelDateInput type="date" value={excelFrom} onChange={e => setExcelFrom(e.target.value)} />
            </ExcelDateRow>
            <ExcelDateRow>
              <ExcelDateLabel>종료일</ExcelDateLabel>
              <ExcelDateInput type="date" value={excelTo} onChange={e => setExcelTo(e.target.value)} />
            </ExcelDateRow>
            <ExcelBtnRow>
              <ExcelCancelBtn onClick={() => setExcelModal(false)}>취소</ExcelCancelBtn>
              <ExcelDownloadBtn onClick={downloadExcel}>다운로드</ExcelDownloadBtn>
            </ExcelBtnRow>
          </ExcelModalBox>
        </Overlay>
      )}

      {bankModal && (
        <Overlay onClick={() => setBankModal(false)}>
          <Modal onClick={e => e.stopPropagation()} style={{ width: 480, maxWidth: 'calc(100vw - 32px)' }}>
            <ModalHeader>
              <ModalTitle>입금계좌 관리</ModalTitle>
              <CloseBtn onClick={() => setBankModal(false)}><IoClose size={22} /></CloseBtn>
            </ModalHeader>
            <ModalBody>
              {bankAccounts.length > 0 ? (
                <BankList>
                  {bankAccounts.map(a => (
                    <BankItem key={a.id}>
                      <BankInfo>
                        <BankName>{a.bank}</BankName>
                        <BankDetail>{a.accountNo} · {a.holder}</BankDetail>
                      </BankInfo>
                      <BankDeleteBtn onClick={() => deleteBankAccount(a.id)}>
                        <IoTrashOutline size={16} />
                      </BankDeleteBtn>
                    </BankItem>
                  ))}
                </BankList>
              ) : (
                <EmptyBank>등록된 계좌가 없습니다</EmptyBank>
              )}

              <BankFormTitle>
                <IoAddCircleOutline size={16} />
                새 계좌 추가
              </BankFormTitle>
              <BankFormRow>
                <BankFormInput
                  placeholder="은행명"
                  value={newBank.bank}
                  onChange={e => setNewBank(prev => ({ ...prev, bank: e.target.value }))}
                  style={{ flex: 1 }}
                />
                <BankFormInput
                  placeholder="계좌번호"
                  value={newBank.accountNo}
                  onChange={e => setNewBank(prev => ({ ...prev, accountNo: e.target.value }))}
                  style={{ flex: 2 }}
                />
                <BankFormInput
                  placeholder="예금주"
                  value={newBank.holder}
                  onChange={e => setNewBank(prev => ({ ...prev, holder: e.target.value }))}
                  style={{ flex: 1 }}
                />
              </BankFormRow>
              <BankAddBtn onClick={addBankAccount} disabled={!newBank.bank || !newBank.accountNo || !newBank.holder}>
                계좌 추가
              </BankAddBtn>
            </ModalBody>
          </Modal>
        </Overlay>
      )}

      {/* 상태 변경 확인 모달 */}
      {confirmModal && (
        <Overlay onClick={() => setConfirmModal(null)}>
          <Modal onClick={e => e.stopPropagation()} style={{ width: 420, maxWidth: 'calc(100vw - 32px)' }}>
            <ModalHeader>
              <ModalTitle>상태 변경 확인</ModalTitle>
              <CloseBtn onClick={() => setConfirmModal(null)}><IoClose size={22} /></CloseBtn>
            </ModalHeader>
            <ModalBody>
              <ConfirmIcon>
                <Badge $color={ORDER_STATUS[confirmModal.newStatus]?.color || '#8C9AA4'}>
                  {ORDER_STATUS[confirmModal.newStatus]?.label || confirmModal.newStatus}
                </Badge>
              </ConfirmIcon>
              <ConfirmMessage>{confirmModal.message}</ConfirmMessage>
              {confirmModal.newStatus === 'shipped' && (
                <ShippingFields>
                  <ShippingLabel>택배사</ShippingLabel>
                  <ShippingInput
                    placeholder="예: CJ대한통운, 한진택배, 로젠택배"
                    value={confirmModal.courier}
                    onChange={e => setConfirmModal(prev => ({ ...prev, courier: e.target.value }))}
                  />
                  <ShippingLabel>운송장번호</ShippingLabel>
                  <ShippingInput
                    placeholder="운송장번호를 입력하세요"
                    value={confirmModal.trackingNo}
                    onChange={e => setConfirmModal(prev => ({ ...prev, trackingNo: e.target.value }))}
                  />
                </ShippingFields>
              )}
              <QuoteBtnRow>
                <QuoteBtn $cancel onClick={() => setConfirmModal(null)}>취소</QuoteBtn>
                <QuoteBtn
                  onClick={executeStatusChange}
                  disabled={confirmModal.newStatus === 'shipped' && (!confirmModal.courier || !confirmModal.trackingNo)}
                >확인</QuoteBtn>
              </QuoteBtnRow>
            </ModalBody>
          </Modal>
        </Overlay>
      )}
    </Container>
  );
};

export default AdminOrdersPage;

const Pagination = styled.div`display: flex; align-items: center; justify-content: center; gap: 4px; padding: 20px 0 8px;`;
const PageBtn = styled.button`
  width: 32px; height: 32px; border-radius: 8px; font-size: 14px;
  background: #fff; color: #666; border: none;
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
  &:disabled { opacity: 0.3; cursor: default; }
  &:hover:not(:disabled) { background: #F0F0EC; }
`;
const PageNum = styled.button`
  width: 32px; height: 32px; border-radius: 8px; font-size: 13px; font-weight: 500;
  border: none; display: flex; align-items: center; justify-content: center;
  background: ${p => p.$active ? '#4A6741' : '#fff'};
  color: ${p => p.$active ? '#fff' : '#666'};
  box-shadow: ${p => p.$active ? 'none' : '0 1px 3px rgba(0,0,0,0.06)'};
  &:hover { background: ${p => p.$active ? '#3B5534' : '#F0F0EC'}; }
`;
const PageDots = styled.span`font-size: 13px; color: #8C9AA4; padding: 0 4px;`;
const PageInfo = styled.span`font-size: 12px; color: #8C9AA4; margin-left: 12px;`;
const Container = styled.div`padding: 32px;`;
const PageHeader = styled.div`display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px;`;
const SearchBox = styled.div`display: flex; align-items: center; gap: 8px; background: #fff; border: none; border-radius: 10px; padding: 8px 14px; width: 260px; box-shadow: 0 1px 3px rgba(0,0,0,0.06);`;
const SearchInput = styled.input`border: none; outline: none; font-size: 13px; flex: 1; background: transparent;`;

const HeaderBtnGroup = styled.div`display: flex; gap: 8px;`;
const BankMgmtBtn = styled.button`
  display: flex; align-items: center; gap: 6px; padding: 8px 16px; border-radius: 10px;
  font-size: 13px; font-weight: 400; border: none; cursor: pointer;
  background: #fff; color: #4A6741; box-shadow: 0 1px 3px rgba(0,0,0,0.06);
  &:hover { background: #F0F0EC; }
`;
const ExcelBtn = styled.button`
  display: flex; align-items: center; gap: 6px; padding: 8px 16px; border-radius: 10px;
  font-size: 13px; font-weight: 400; border: none; cursor: pointer;
  background: #4A6741; color: #fff; box-shadow: 0 1px 3px rgba(0,0,0,0.06);
  &:hover { opacity: 0.9; }
`;
const ExcelModalBox = styled.div`
  background: #fff; border-radius: 16px; padding: 28px 24px; width: 340px;
`;
const ExcelDesc = styled.p`
  font-size: 13px; color: #8C9AA4; margin-bottom: 20px;
`;
const ExcelDateRow = styled.div`
  display: flex; align-items: center; gap: 12px; margin-bottom: 12px;
`;
const ExcelDateLabel = styled.label`
  font-size: 13px; color: #5A6B75; width: 50px; flex-shrink: 0;
`;
const ExcelDateInput = styled.input`
  flex: 1; padding: 10px 12px; border: 1px solid #E0E0E0; border-radius: 8px;
  font-size: 14px; color: #1E2A32;
`;
const ExcelBtnRow = styled.div`
  display: flex; gap: 10px; margin-top: 20px;
`;
const ExcelCancelBtn = styled.button`
  flex: 1; padding: 12px; border-radius: 10px; font-size: 14px;
  border: 1px solid #E0E0E0; background: #fff; color: #666; cursor: pointer;
`;
const ExcelDownloadBtn = styled.button`
  flex: 1; padding: 12px; border-radius: 10px; font-size: 14px;
  border: none; background: #4A6741; color: #fff; cursor: pointer;
  &:hover { opacity: 0.9; }
`;

const TabRow = styled.div`display: flex; gap: 6px; margin-bottom: 20px; flex-wrap: wrap;`;
const Tab = styled.button`
  padding: 8px 16px; border-radius: 10px; font-size: 13px; font-weight: 400;
  display: flex; align-items: center; gap: 5px;
  background: ${p => p.$active ? '#8C9AA4' : '#fff'};
  color: ${p => p.$active ? '#fff' : '#666'};
  border: none;
  box-shadow: ${p => p.$active ? 'none' : '0 1px 3px rgba(0,0,0,0.06)'};
`;
const TabCount = styled.span`font-size: 10px; opacity: 0.8;`;

const TableWrap = styled.div`background: #fff; border-radius: 14px; box-shadow: 0 1px 3px rgba(0,0,0,0.05); overflow: hidden;`;
const Table = styled.table`width: 100%; border-collapse: collapse;`;
const Tr = styled.tr`
  position: relative; transition: all 0.15s;
  &:not(:last-child) td { border-bottom: 1px solid #F0F0EC; }
  &:hover td { background: #EDF2EB; }
  &:hover td:first-child { box-shadow: inset 3px 0 0 #4A6741; }
  &:active td { background: #E3EBDF; }
`;
const Th = styled.th`text-align: left; font-size: 14px; color: #8C9AA4; font-weight: 400; padding: 14px 16px; border-bottom: 1px solid #E8E8E4; background: #FAFAF7;`;
const Td = styled.td`font-size: ${p => p.$sub ? '13px' : '15px'}; color: ${p => p.$sub ? '#8C9AA4' : '#1E2A32'}; padding: 16px 16px; font-weight: ${p => p.$bold ? '600' : '400'}; white-space: nowrap;`;
const TdProduct = styled.td`
  font-size: 13px; color: #1E2A32; padding: 16px 16px; font-weight: 400;
  white-space: normal; max-width: 240px; line-height: 1.4;
  overflow: hidden;
  word-break: break-all;
`;
const Empty = styled.div`text-align: center; padding: 48px; color: #8C9AA4; font-size: 15px;`;
const Badge = styled.span`font-size: 14px; padding: 5px 14px; border-radius: 10px; font-weight: 500; background: ${p => p.$color}20; color: ${p => p.$color};`;
const Select = styled.select`font-size: 14px; padding: 6px 10px; border-radius: 8px; border: 1px solid #D9DDD6; background: #fff; color: #1E2A32; cursor: pointer;`;
const CancelledRow = styled.div`display: flex; align-items: center; gap: 8px;`;
const CancelledText = styled.span`font-size: 14px; color: #EF5350; font-weight: 400;`;
const DeleteBtn = styled.button`
  display: flex; align-items: center; justify-content: center;
  width: 28px; height: 28px; border-radius: 6px;
  background: #FFF0EE; color: #EF5350;
  &:hover { background: #FFDDDA; }
`;

const Overlay = styled.div`position: fixed; inset: 0; background: rgba(0,0,0,0.4); display: flex; align-items: center; justify-content: center; z-index: 200;`;
const Modal = styled.div`background: #fff; border-radius: 16px; width: 480px; max-height: 90vh; overflow-y: auto;`;
const ModalHeader = styled.div`display: flex; justify-content: space-between; align-items: center; padding: 20px 24px; border-bottom: 1px solid #F0F0EC;`;
const ModalTitle = styled.h3`font-size: 18px; font-weight: 500;`;
const CloseBtn = styled.button`color: #8C9AA4; display: flex;`;
const ModalBody = styled.div`padding: 24px;`;
const DetailTable = styled.table`width: 100%; border-collapse: collapse; border-top: 1.5px solid #E0E0DC;`;
const DRow = styled.tr`td, th { border-bottom: 1.5px solid #E0E0DC; }`;
const DLabel = styled.th`width: 100px; font-size: 14px; color: #5A6B75; font-weight: 400; text-align: left; padding: 14px 16px; background: #F0F0EC; white-space: nowrap;`;
const DVal = styled.td`font-size: 15px; color: #1E2A32; font-weight: 400; padding: 14px 16px;`;
const DOptionList = styled.p`font-size: 13px; color: #8C9AA4; margin-top: 4px; font-weight: 400;`;
const DAddrLabel = styled.span`
  display: inline-block; font-size: 11px; color: #4A6741; background: #EDF2EB;
  padding: 1px 8px; border-radius: 4px; margin-bottom: 4px;
`;
const DDeliveryTag = styled.span`
  display: inline-block; font-size: 12px; font-weight: 600; color: #C62828; background: #FFEBEE;
  padding: 2px 10px; border-radius: 4px; margin-bottom: 4px; margin-left: 4px;
`;
const DDeliveryMsg = styled.p`
  font-size: 12px; color: #8C9AA4; margin-top: 6px; font-style: italic;
`;

const InvoiceBadge = styled.span`
  display: inline-block;
  font-size: 12px;
  font-weight: 500;
  padding: 2px 10px;
  border-radius: 4px;
  color: ${({ $requested }) => $requested ? '#2E7D32' : '#9E9E9E'};
  background: ${({ $requested }) => $requested ? '#E8F5E9' : '#F5F5F5'};
`;

/* 견적 모달 */
const QuoteModal = styled(Modal)`width: 580px;`;
const QuoteTable = styled.table`width: 100%; border-collapse: collapse; margin-bottom: 16px; table-layout: fixed;`;
const QuoteTh = styled.th`
  text-align: ${p => p.$left ? 'left' : 'center'}; font-size: 12px; color: #8C9AA4;
  font-weight: 400; padding: 10px 12px; border-bottom: 1px solid #E8E8E4; background: #FAFAF7;
  &:nth-child(1) { width: auto; }
  &:nth-child(2) { width: 50px; }
  &:nth-child(3) { width: 130px; }
  &:nth-child(4) { width: 90px; }
`;
const QuoteTd = styled.td`
  text-align: ${p => p.$left ? 'left' : 'center'}; font-size: 13px; color: #1E2A32;
  padding: 10px 12px; border-bottom: 1px solid #F0F0EC; font-weight: 400;
`;
const QuoteOptionInfo = styled.p`
  font-size: 11px; color: #8C9AA4; margin-top: 2px; font-weight: 400;
`;
const QuoteProductName = styled.p`
  font-size: 13px; color: #1E2A32; margin-bottom: 4px;
`;
const QuoteOptionLabel = styled.span`
  font-size: 12px; color: #4A6741; background: #EDF2EB; padding: 2px 8px; border-radius: 6px;
`;
const QuoteItemInput = styled.input`
  width: 110px; padding: 6px 10px; font-size: 13px; border: 1px solid #D9DDD6;
  border-radius: 8px; outline: none; text-align: right; box-sizing: border-box;
  &:focus { border-color: #4A6741; }
`;
const QuoteTotalBar = styled.div`
  display: flex; justify-content: space-between; align-items: center;
  padding: 14px 12px; background: #F8F8F5; border-radius: 10px; margin-bottom: 20px;
`;
const QuoteTotalLabel = styled.span`font-size: 14px; color: #1E2A32; font-weight: 500;`;
const QuoteTotalValue = styled.span`font-size: 18px; color: #4A6741; font-weight: 500;`;
const QuoteLabel = styled.div`font-size: 13px; color: #8C9AA4; margin-bottom: 8px;`;
const AccountSelect = styled.select`
  width: 100%; padding: 10px 12px; font-size: 13px; border: 1px solid #D9DDD6;
  border-radius: 10px; outline: none; background: #fff; color: #1E2A32; box-sizing: border-box;
  &:focus { border-color: #4A6741; }
`;
const NoAccountMsg = styled.div`font-size: 13px; color: #E85830; padding: 10px 0;`;
const QuoteBtnRow = styled.div`display: flex; gap: 10px; margin-top: 20px;`;
const QuoteBtn = styled.button`
  flex: 1; padding: 12px; border-radius: 10px; font-size: 14px; font-weight: 500; border: none; cursor: pointer;
  background: ${p => p.$cancel ? '#F0F0EC' : '#4A6741'};
  color: ${p => p.$cancel ? '#666' : '#fff'};
  &:hover { opacity: 0.9; }
  &:disabled { opacity: 0.4; cursor: default; }
`;

/* 입금계좌 관리 모달 */
const BankList = styled.div`margin-bottom: 20px;`;
const BankItem = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 12px 14px; border-radius: 10px; background: #F8F8F5; margin-bottom: 8px;
`;
const BankInfo = styled.div``;
const BankName = styled.div`font-size: 13px; color: #1E2A32; font-weight: 500;`;
const BankDetail = styled.div`font-size: 12px; color: #8C9AA4; margin-top: 2px;`;
const BankDeleteBtn = styled.button`
  color: #EF5350; display: flex; padding: 6px; border-radius: 8px;
  &:hover { background: #FFF0F0; }
`;
const EmptyBank = styled.div`text-align: center; padding: 24px; color: #8C9AA4; font-size: 13px; margin-bottom: 20px;`;
const BankFormTitle = styled.div`
  display: flex; align-items: center; gap: 6px; font-size: 13px; color: #4A6741;
  font-weight: 500; margin-bottom: 10px; padding-top: 16px; border-top: 1px solid #F0F0EC;
`;
const BankFormRow = styled.div`display: flex; gap: 8px; margin-bottom: 10px; flex-wrap: wrap;`;
const BankFormInput = styled.input`
  padding: 10px 12px; font-size: 13px; border: 1px solid #D9DDD6;
  border-radius: 10px; outline: none; box-sizing: border-box; min-width: 0;
  &:focus { border-color: #4A6741; }
`;
const BankAddBtn = styled.button`
  width: 100%; padding: 10px; border-radius: 10px; font-size: 13px; font-weight: 500;
  border: none; cursor: pointer; background: #4A6741; color: #fff;
  &:hover { opacity: 0.9; }
  &:disabled { opacity: 0.4; cursor: default; }
`;

const ConfirmIcon = styled.div`text-align: center; margin-bottom: 16px;`;
const ConfirmMessage = styled.p`
  font-size: 14px; color: #1E2A32; line-height: 1.7; text-align: center;
  white-space: pre-line; margin-bottom: 4px;
`;
const ShippingFields = styled.div`margin: 16px 0 4px;`;
const ShippingLabel = styled.div`font-size: 13px; color: #8C9AA4; margin-bottom: 6px; margin-top: 12px;`;
const ShippingInput = styled.input`
  width: 100%; padding: 11px 14px; font-size: 14px; border: 1px solid #D9DDD6;
  border-radius: 10px; outline: none; box-sizing: border-box;
  &:focus { border-color: #4A6741; }
`;
